
  import {
    defineComponent,
    ref,
    onMounted,
    computed,
    onBeforeUnmount,
    reactive,
    watch,
  } from 'vue';
  import { useRouter } from 'vue-router';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Datatable from '@/components/kt-datatable/KTDatatable.vue';
  import AuditLogsAdvancedSearch from '@/components/search/AuditLogsAdvancedSearch.vue';

  export default defineComponent({
    name: 'audit-logs-listing',
    components: {
      Datatable,
      AuditLogsAdvancedSearch,
    },
    setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const { can } = useAbility();

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const loading = ref(false);
      const selectedLog = ref<any>(null);
      const showExportBtn = ref(false);

      const excelHeader = reactive({
        ID: 'id',
        'Entity Type': 'entityType',
        'Entity ID': 'entityId',
        Action: 'action',
        'User Name': 'userName',
        'User Email': 'userEmail',
        'User ID': 'userId',
        'Changed Fields': {
          field: 'changedFields',
          callback: (value) => {
            return Array.isArray(value) ? value.join(', ') : '';
          },
        },
        'IP Address': 'ipAddress',
        Timestamp: {
          field: 'createdAt',
          callback: (value) => {
            return new Date(value).toLocaleString('en-GB');
          },
        },
      });

      const exportFileName = computed(() => {
        const entityType =
          store.getters.getAuditLogsFilter?.entityType || 'all';
        const date = new Date().toISOString().slice(0, 10);
        return `Audit_Logs_${entityType}_${date}.xls`;
      });

      const tableHeader = ref([
        {
          name: translate('actions'),
          key: 'actions',
          sortable: false,
        },
        {
          name: translate('ID'),
          key: 'id',
          sortable: true,
        },
        {
          name: translate('entityType'),
          key: 'entityType',
          sortable: true,
        },
        {
          name: translate('entityId'),
          key: 'entityId',
          sortable: true,
        },
        {
          name: translate('action'),
          key: 'action',
          sortable: true,
        },
        {
          name: translate('user'),
          key: 'user',
          sortable: false,
        },
        {
          name: translate('changedFields'),
          key: 'changedFields',
          sortable: false,
        },
        {
          name: translate('timestamp'),
          key: 'createdAt',
          sortable: true,
        },
      ]);

      const fetchAuditLogs = async () => {
        loading.value = true;
        await store.dispatch(Actions.GET_AUDIT_LOGS);
        loading.value = false;
      };

      const pageChanged = async (val: number) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_AUDIT_LOGS_CURRENT_PAGE, val);
        await store.dispatch(Actions.GET_AUDIT_LOGS);
        loading.value = false;
      };

      const onTypeChange = async (values) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_AUDIT_LOGS_FILTER, values);
        await store.dispatch(Actions.UPDATE_AUDIT_LOGS_CURRENT_PAGE, 1);
        await store.dispatch(Actions.GET_AUDIT_LOGS);

        // Show export button only if entityType is specified
        showExportBtn.value = !!values.entityType;

        loading.value = false;
      };

      const onReset = async (value) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_AUDIT_LOGS_FILTER, value);
        await store.dispatch(Actions.UPDATE_AUDIT_LOGS_CURRENT_PAGE, 1);
        await store.dispatch(Actions.GET_AUDIT_LOGS);

        showExportBtn.value = false;

        loading.value = false;
      };

      const viewDetails = (log) => {
        selectedLog.value = log;
      };

      const closeDetails = () => {
        selectedLog.value = null;
      };

      const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB');
      };

      const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-GB');
      };

      const formatEntityType = (entityType: string) => {
        return entityType
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      };

      onMounted(async () => {
        setCurrentPageBreadcrumbs(translate('auditLogs'), [
          translate('auditLogs'),
        ]);
        await fetchAuditLogs();

        const filter = store.getters.getAuditLogsFilter;
        showExportBtn.value = !!filter?.entityType;

        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');
      });

      onBeforeUnmount(() => {
        store.dispatch(Actions.RESET_AUDIT_LOGS_STORE);
      });

      return {
        tableData: computed(() => store.getters.auditLogsList),
        tableHeader,
        totalItems: computed(() => store.getters.auditLogsListCount),
        translate,
        pageChanged,
        loading,
        can,
        selectedLog,
        viewDetails,
        closeDetails,
        formatDate,
        formatTime,
        formatEntityType,
        onTypeChange,
        onReset,
        showExportBtn,
        excelHeader,
        exportFileName,
      };
    },
  });
